import { useMemo, useState } from "react";
import useElementWidth from "./useElementWidth";
import { maxAxisLabels, rangeTicks } from "../domain/chartAxes";
import { formatMonth, formatShortDate } from "../domain/insightsPresentation";
import { TREND_TABLE_CAPTION, TREND_VALUE_HEADER, formatNumber, formatPointDetail, formatPointReadout, formatTrendPart, formatValueWithUnit } from "../domain/exercisePerformancePresentation";

/*
 * Performance / e1RM step chart (EP blueprint §6.7, canon §13.3–§13.4).
 * Pure presentation of the API's facts: the points, trend segments, neighbour
 * trends and monthly buckets arrive computed (EP-4); nothing is aggregated,
 * smoothed or interpolated here. X is true time; Y is the series scale padded
 * to the data (not anchored at 0). Each trend segment is a step path of `H` /
 * `V` commands only, so a break between segments is never bridged and the
 * line never shows a value that was not lifted. No gradient, no area, no dash,
 * no curve. A segment holding a single breakpoint has no step to draw, so its
 * median is marked on its own (a hollow diamond at that date and value, never
 * joined to a neighbouring segment) instead of a zero-length path. The SVG is
 * hidden from assistive technology; visually hidden tables list every value
 * and every trend value.
 */

export const CHART_HEIGHT = 200;
const PAD = { top: 12, right: 12, bottom: 24, left: 40 };
// Half the side of the isolated-median diamond (a square turned 45°).
const MARKER_HALF = 6;
const DAY_MS = 24 * 60 * 60 * 1000;

function timeOf(dateKey) {
  const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(String(dateKey || ""));
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3] || 1));
}

function pointKey(point) {
  return `${point.executionId}|${point.itemPosition ?? ""}|${point.setIndex ?? ""}`;
}

// One trend segment: a step path, or a standalone median marker when the
// segment has a single breakpoint (a lone `M` draws nothing).
function TrendSegment({ segment, xOf, yOf, className, ...rest }) {
  if (segment.length === 1) {
    const x = xOf(segment[0].date);
    const y = yOf(segment[0].value);
    return (
      <rect
        className={`${className} lz-insights-ep-chart__trend-marker`}
        x={x - MARKER_HALF}
        y={y - MARKER_HALF}
        width={MARKER_HALF * 2}
        height={MARKER_HALF * 2}
        transform={`rotate(45 ${x} ${y})`}
        pointerEvents="none"
        data-date={segment[0].date}
        data-value={segment[0].value}
        {...rest}
      />
    );
  }
  return <path className={className} d={stepPath(segment, xOf, yOf)} {...rest} />;
}

function stepPath(segment, xOf, yOf) {
  return segment.map((point, index) => (index === 0
    ? `M${xOf(point.date)} ${yOf(point.value)}`
    : `H${xOf(point.date)} V${yOf(point.value)}`)).join(" ");
}

export default function PerformanceStepChart({
  mode = "performance",
  points = [],
  trend = null,
  neighbours = [],
  buckets = null,
  dividerDate = null,
  unit,
  reps = null,
  summary,
  testId = "ep-step-chart",
}) {
  const [ref, width] = useElementWidth();
  const [active, setActive] = useState(null);
  const monthly = Array.isArray(buckets);

  const geometry = useMemo(() => {
    const bucketPoints = (buckets || []).map((bucket) => ({ date: `${bucket.month}-01`, value: bucket.value }));
    const trendPoints = (trend?.segments || []).flat();
    const neighbourPoints = neighbours.flatMap((entry) => (entry.trend?.segments || []).flat());
    const all = [...points, ...bucketPoints, ...trendPoints, ...neighbourPoints].filter((point) => timeOf(point.date) !== null && point.value !== null && point.value !== undefined);
    if (!all.length) return null;
    const times = all.map((point) => timeOf(point.date));
    let minTime = Math.min(...times);
    let maxTime = Math.max(...times);
    if (minTime === maxTime) {
      minTime -= 3 * DAY_MS;
      maxTime += 3 * DAY_MS;
    }
    const values = all.map((point) => Number(point.value));
    const ticks = rangeTicks(Math.min(...values), Math.max(...values));
    const plotWidth = Math.max(1, width - PAD.left - PAD.right);
    const plotHeight = CHART_HEIGHT - PAD.top - PAD.bottom;
    const xOf = (dateKey) => Math.round((PAD.left + ((timeOf(dateKey) - minTime) / (maxTime - minTime)) * plotWidth) * 10) / 10;
    const yOf = (value) => Math.round((PAD.top + (1 - (Number(value) - ticks[0]) / (ticks[ticks.length - 1] - ticks[0])) * plotHeight) * 10) / 10;
    // Evenly spaced date labels across the time span.
    const labelCount = maxAxisLabels(plotWidth, 6);
    const labels = Array.from({ length: labelCount }, (_, index) => {
      const time = minTime + ((maxTime - minTime) * index) / Math.max(1, labelCount - 1);
      const dateKey = new Date(time).toISOString().slice(0, 10);
      return { x: xOf(dateKey), text: monthly ? formatMonth(dateKey, { withYear: true }) : formatShortDate(dateKey) };
    });
    return { xOf, yOf, ticks, labels, minTime, maxTime };
  }, [buckets, monthly, neighbours, points, trend, width]);

  const rows = monthly
    ? (buckets || []).map((bucket) => ({ key: bucket.month, date: formatMonth(`${bucket.month}-01`, { withYear: true }), value: formatValueWithUnit(bucket.value, unit), detail: `${bucket.count} ${bucket.count === 1 ? "session" : "sessions"}` }))
    : points.map((point) => ({ key: pointKey(point), date: formatShortDate(point.date), value: formatValueWithUnit(point.value, unit), detail: mode === "e1rm" ? "" : formatPointDetail(point) }));
  // Every trend value the API computed, by segment (the SVG is aria-hidden).
  const trendSegments = trend?.segments || [];
  const trendRows = trendSegments.flatMap((segment, segmentIndex) => segment.map((point, pointIndex) => ({
    key: `${segmentIndex}-${pointIndex}`,
    date: monthly ? formatMonth(point.date, { withYear: true }) : formatShortDate(point.date),
    value: formatValueWithUnit(point.value, unit),
    detail: formatTrendPart(segmentIndex, trendSegments.length),
  })));
  // The tapped point, by identity: switching rep count or timeframe clears it.
  const activePoint = monthly ? null : points.find((point) => pointKey(point) === active) || null;

  const divider = geometry && dividerDate && timeOf(dividerDate) > geometry.minTime && timeOf(dividerDate) < geometry.maxTime
    ? geometry.xOf(dividerDate)
    : null;

  return (
    <figure className="lz-insights-ep-chart" data-testid={testId} data-mode={mode}>
      <div className="lz-insights-ep-chart__canvas" ref={ref} style={{ height: CHART_HEIGHT }}>
        {geometry ? (
          <svg
            className="lz-insights-ep-chart__svg"
            width={width}
            height={CHART_HEIGHT}
            viewBox={`0 0 ${width} ${CHART_HEIGHT}`}
            aria-hidden="true"
            focusable="false"
          >
            {geometry.ticks.map((tick) => (
              <g key={tick}>
                <line className="lz-insights-ep-chart__grid" x1={PAD.left} x2={width - PAD.right} y1={geometry.yOf(tick)} y2={geometry.yOf(tick)} />
                <text className="lz-insights-ep-chart__tick" x={PAD.left - 6} y={geometry.yOf(tick) + 3} textAnchor="end">{formatNumber(tick)}</text>
              </g>
            ))}
            {geometry.labels.map((label, index) => (
              <text
                key={`${label.text}-${index}`}
                className="lz-insights-ep-chart__tick"
                x={label.x}
                y={CHART_HEIGHT - 6}
                textAnchor={index === 0 ? "start" : index === geometry.labels.length - 1 ? "end" : "middle"}
              >
                {label.text}
              </text>
            ))}
            {divider !== null ? (
              <line className="lz-insights-ep-chart__divider" data-testid="ep-chart-divider" x1={divider} x2={divider} y1={PAD.top} y2={CHART_HEIGHT - PAD.bottom} />
            ) : null}

            {neighbours.map((entry) => (entry.trend?.segments || []).filter((segment) => segment.length > 1).map((segment, index) => (
              <TrendSegment
                key={`n${entry.reps}-${index}`}
                segment={segment}
                xOf={geometry.xOf}
                yOf={geometry.yOf}
                className="lz-insights-ep-chart__trend lz-insights-ep-chart__trend--neighbour"
                data-neighbour={entry.reps}
              />
            )))}
            {(trend?.segments || []).map((segment, index) => (segment.length > 1 ? (
              <TrendSegment key={`t${index}`} segment={segment} xOf={geometry.xOf} yOf={geometry.yOf} className="lz-insights-ep-chart__trend" data-segment={index} />
            ) : null))}

            {monthly ? buckets.map((bucket) => (
              <rect
                key={bucket.month}
                className="lz-insights-ep-chart__bucket"
                x={geometry.xOf(`${bucket.month}-01`) - 4}
                y={geometry.yOf(bucket.value) - 4}
                width={8}
                height={8}
              />
            )) : points.map((point, index) => {
              const x = geometry.xOf(point.date);
              const y = geometry.yOf(point.value);
              const key = pointKey(point);
              const className = [
                "lz-insights-ep-chart__point",
                mode === "e1rm" ? "lz-insights-ep-chart__point--neutral" : `lz-insights-ep-chart__point--${String(point.rirBand || "NONE").toLowerCase()}`,
                point.fill === "HOLLOW" ? "lz-insights-ep-chart__point--hollow" : "",
                point.fill === "MISSING_RIR" ? "lz-insights-ep-chart__point--missing" : "",
                point.emphasis === "FAINT" ? "lz-insights-ep-chart__point--faint" : "",
                active === key ? "lz-insights-ep-chart__point--active" : "",
              ].filter(Boolean).join(" ");
              return (
                <g key={`${key}-${index}`}>
                  <circle className={className} cx={x} cy={y} r={point.emphasis === "OBSERVATION" ? 5 : 4} data-fill={point.fill || "FILLED"} />
                  {/* A larger, invisible target for tapping a point. */}
                  <circle className="lz-insights-ep-chart__hit" cx={x} cy={y} r={12} onClick={() => setActive(active === key ? null : key)} />
                </g>
              );
            })}

            {/* Isolated medians last, on top of a coincident observation; they never take a tap. */}
            {neighbours.map((entry) => (entry.trend?.segments || []).map((segment, index) => (segment.length === 1 ? (
              <TrendSegment
                key={`nm${entry.reps}-${index}`}
                segment={segment}
                xOf={geometry.xOf}
                yOf={geometry.yOf}
                className="lz-insights-ep-chart__trend lz-insights-ep-chart__trend--neighbour"
                data-neighbour={entry.reps}
              />
            ) : null)))}
            {(trend?.segments || []).map((segment, index) => (segment.length === 1 ? (
              <TrendSegment key={`tm${index}`} segment={segment} xOf={geometry.xOf} yOf={geometry.yOf} className="lz-insights-ep-chart__trend" data-segment={index} />
            ) : null))}
          </svg>
        ) : null}
      </div>
      <p className="lz-insights-ep-chart__readout" role="status" data-testid="ep-chart-readout">
        {activePoint
          ? (mode === "e1rm"
            ? `${formatShortDate(activePoint.date)} · ${formatValueWithUnit(activePoint.value, unit)}`
            : formatPointReadout(activePoint, { reps, unit }))
          : ""}
      </p>
      <div className="lz-v2-visually-hidden">
        <table data-testid="ep-chart-table">
          <caption>{summary}</caption>
          <thead>
            <tr>
              <th scope="col">{monthly ? "Month" : "Date"}</th>
              <th scope="col">Value</th>
              <th scope="col">Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>{row.date}</td>
                <td>{row.value}</td>
                <td>{row.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {trendRows.length ? (
          <table data-testid="ep-chart-trend-table">
            <caption>{`${TREND_TABLE_CAPTION}: ${summary}`}</caption>
            <thead>
              <tr>
                <th scope="col">{monthly ? "Month" : "Date"}</th>
                <th scope="col">{TREND_VALUE_HEADER}</th>
                <th scope="col">Details</th>
              </tr>
            </thead>
            <tbody>
              {trendRows.map((row) => (
                <tr key={row.key}>
                  <td>{row.date}</td>
                  <td>{row.value}</td>
                  <td>{row.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </figure>
  );
}
